import { createTheme } from '@mui/material/styles';

const defaultTheme = createTheme();
const customShadows = [...defaultTheme.shadows];
customShadows[0] = 'none';
customShadows[1] = '0 1px 2px 0 rgb(0 0 0 / 0.05)';
customShadows[2] = '0 1px 3px 0 rgb(0 0 0 / 0.1), 0 1px 2px -1px rgb(0 0 0 / 0.1)';
customShadows[3] = '0 4px 6px -1px rgb(0 0 0 / 0.1), 0 2px 4px -2px rgb(0 0 0 / 0.1)';
customShadows[4] = '0 10px 15px -3px rgb(0 0 0 / 0.1), 0 4px 6px -4px rgb(0 0 0 / 0.1)';
customShadows[5] = '0 20px 25px -5px rgb(0 0 0 / 0.1), 0 8px 10px -6px rgb(0 0 0 / 0.1)';

const theme = createTheme({
  palette: {
    primary: {
      main: '#0F3D3E',
      light: '#1b5a5c',
      dark: '#0a2829',
      contrastText: '#ffffff',
    },
    secondary: {
      main: '#f3f4f6',
      light: '#ffffff',
      dark: '#e5e7eb',
      contrastText: '#1f2937',
    },
    success: {
      main: '#10b981',
      light: '#34d399',
      dark: '#059669',
    },
    warning: {
      main: '#f59e0b',
      light: '#fbbf24',
      dark: '#d97706',
    },
    info: {
      main: '#3b82f6',
      light: '#60a5fa',
      dark: '#2563eb',
    },
    error: {
      main: '#ef4444',
      light: '#f87171',
      dark: '#dc2626',
    },
    grey: {
      50: '#f8fafc',
      100: '#f1f5f9',
      200: '#e2e8f0',
      300: '#cbd5e1',
      400: '#94a3b8',
      500: '#64748b',
      600: '#475569',
      700: '#334155',
      800: '#1e293b',
      900: '#0f172a',
    },
    background: {
      default: '#f8fafc',
      paper: '#ffffff',
    },
    text: {
      primary: '#1e293b',
      secondary: '#64748b',
    },
  },
  shape: {
    borderRadius: 12,
  },
  shadows: customShadows,
  typography: {
    fontFamily: '"Inter", "Roboto", "Helvetica", "Arial", sans-serif',
    fontWeightLight: 300,
    fontWeightRegular: 400,
    fontWeightMedium: 500,
    fontWeightBold: 600,
    h1: {
      fontSize: '2.5rem',
      fontWeight: 600,
      lineHeight: 1.2,
    },
    h2: {
      fontSize: '2rem',
      fontWeight: 600,
      lineHeight: 1.2,
    },
    h3: {
      fontSize: '1.75rem',
      fontWeight: 600,
      lineHeight: 1.2,
    },
    h4: {
      fontSize: '1.5rem',
      fontWeight: 600,
      lineHeight: 1.2,
    },
    h5: {
      fontSize: '1.25rem',
      fontWeight: 600,
      lineHeight: 1.2,
    },
    h6: {
      fontSize: '1.125rem',
      fontWeight: 600,
      lineHeight: 1.2,
    },
    subtitle1: {
      fontSize: '1rem',
      fontWeight: 500,
      lineHeight: 1.5,
    },
    subtitle2: {
      fontSize: '0.875rem',
      fontWeight: 500,
      lineHeight: 1.57,
    },
    body1: {
      fontSize: '1rem',
      lineHeight: 1.5,
    },
    body2: {
      fontSize: '0.875rem',
      lineHeight: 1.43,
    },
    button: {
      textTransform: 'none',
      fontWeight: 600,
    },
  },
  components: {
    MuiAppBar: {
      defaultProps: {
        elevation: 0,
      },
      styleOverrides: {
        root: {
          background: 'linear-gradient(135deg, #1b5a5c 0%, #0a2829 100%)',
          color: '#ffffff',
          boxShadow: '0 4px 6px -1px rgba(15, 61, 62, 0.1), 0 2px 4px -2px rgba(15, 61, 62, 0.06)',
          '&:hover': {
            background: 'linear-gradient(135deg, #328284 0%, #0F3D3E 100%)',
            boxShadow: '0 10px 15px -3px rgba(15, 61, 62, 0.2), 0 4px 6px -4px rgba(15, 61, 62, 0.2)',
          },
        },
      },
    },
    MuiButton: {
      defaultProps: {
        disableElevation: true,
      },
      styleOverrides: {
        root: {
          textTransform: 'none',
          fontWeight: 600,
          borderRadius: 12,
          padding: '0.5rem 1.25rem',
        },
        contained: {
          background: 'linear-gradient(135deg, #1b5a5c 0%, #0a2829 100%)',
          color: '#ffffff',
          borderRadius: 12,
          boxShadow: '0 4px 14px rgba(15, 61, 62, 0.25)',
          '&:hover': {
            background: 'linear-gradient(135deg, #328284 0%, #0F3D3E 100%)',
            boxShadow: '0 6px 18px rgba(15, 61, 62, 0.35)',
          },
        },
        containedSecondary: {
          background: '#ffffff',
          color: '#1f2937',
          boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -2px rgba(0, 0, 0, 0.06)',
          '&:hover': {
            background: '#f9fafb',
            boxShadow: '0 10px 15px -3px rgba(0, 0, 0, 0.1), 0 4px 6px -4px rgba(0, 0, 0, 0.05)',
          },
        },
        outlined: {
          borderWidth: '2px',
          borderColor: '#328284',
          color: '#0F3D3E',
          '&:hover': {
            borderWidth: '2px',
            borderColor: '#0F3D3E',
            backgroundColor: 'rgba(15, 61, 62, 0.04)',
          },
        },
        outlinedSecondary: {
          borderWidth: '2px',
          borderColor: '#e5e7eb',
          color: '#1f2937',
          '&:hover': {
            borderWidth: '2px',
            borderColor: '#d1d5db',
            backgroundColor: 'rgba(0, 0, 0, 0.02)',
          },
        },
      },
    },
    MuiCard: {
      defaultProps: {
        elevation: 0,
      },
      styleOverrides: {
        root: {
          borderRadius: 12,
          border: '1px solid #f1f5f9',
          boxShadow: '0 4px 6px -1px rgba(15, 61, 62, 0.1), 0 2px 4px -2px rgba(15, 61, 62, 0.06)',
          '&:hover': {
            boxShadow: '0 10px 15px -3px rgba(15, 61, 62, 0.2), 0 4px 6px -4px rgba(15, 61, 62, 0.2)',
            borderColor: '#328284',
          },
        },
      },
    },
    MuiTableHead: {
      styleOverrides: {
        root: {
          backgroundColor: '#ebf4f4',
          '.MuiTableCell-root': {
            color: '#0b2e2f',
            fontSize: '0.75rem',
            fontWeight: 600,
            textTransform: 'uppercase',
            letterSpacing: '0.05em',
          },
        },
      },
    },
    MuiTableRow: {
      styleOverrides: {
        root: {
          '&:hover': {
            backgroundColor: '#ebf4f4',
          },
          '&.Mui-selected': {
            backgroundColor: '#e2efef',
            '&:hover': {
              backgroundColor: '#d2e5e5',
            },
          },
        },
      },
    },
    MuiListItemButton: {
      styleOverrides: {
        root: {
          borderRadius: 8,
          margin: '0.25rem 0.5rem',
          '&.Mui-selected': {
            background: 'linear-gradient(90deg, rgba(15, 61, 62, 0.08), rgba(15, 61, 62, 0.12))',
            '&:hover': {
              background: 'linear-gradient(90deg, rgba(15, 61, 62, 0.12), rgba(15, 61, 62, 0.16))',
            },
            '& .MuiListItemIcon-root': {
              color: '#0F3D3E',
            },
            '& .MuiListItemText-primary': {
              color: '#0b2e2f',
              fontWeight: 600,
            },
          },
          '&:hover': {
            background: 'linear-gradient(90deg, rgba(15, 61, 62, 0.04), rgba(15, 61, 62, 0.08))',
          },
        },
      },
    },
    MuiListItemIcon: {
      styleOverrides: {
        root: {
          color: '#64748b',
          minWidth: 40,
          '& .MuiSvgIcon-root': {
            fontSize: '1.25rem',
          },
        },
      },
    },
  },
});

export default theme;
